import { Observable, Subscription, filter, repeat, take } from 'rxjs';
import {
  Directive,
  ElementRef,
  OnDestroy,
  HostListener,
  Input,
  inject,
  DOCUMENT,
  afterNextRender,
} from '@angular/core';
import { SummaryService } from '../../admin/shared/public-api';

@Directive({
  selector: '[appArticle]',
  standalone: true,
})
export class ArticleDirective implements OnDestroy {
  private element: HTMLElement;
  private document = inject(DOCUMENT);
  children: NodeListOf<Element>;
  plainPath: HTMLElement;
  subscriptions: Subscription[];
  path: HTMLElement;
  currentActiveTitle: string;

  @Input() isMobile = false;
  @Input() manual = false;

  constructor(
    private elementRef: ElementRef,
    private readonly summaryEventsService: SummaryService,
  ) {
    afterNextRender({
      write: () => this.render(),
    });
    this.subscriptions = [];
  }

  private render(): void {
    if (this.isMobile) {
      if (this.path) {
        this.path.style.setProperty('display', 'none');
        this.plainPath.style.setProperty('display', 'none');
        const icons = this.element.getElementsByClassName('icon-container');
        for (let i = 0; i < icons.length; i++) {
          let icon = icons[i] as HTMLElement;
          icon.style.setProperty('display', 'none');
        }
      }
      return;
    } else {
      if (this.path) {
        this.path.style.setProperty('display', 'block');
        this.plainPath.style.setProperty('display', 'block');
        const icons = this.element.getElementsByClassName('icon-container');
        for (let i = 0; i < icons.length; i++) {
          let icon = icons[i] as HTMLElement;
          icon.style.setProperty('display', 'block');
        }
      }
    }

    if (!this.manual) {
      this.initDirective();
    }
  }

  initDirective(): void {
    this.element = this.elementRef.nativeElement as HTMLElement;
    this.children = this.element.querySelectorAll(
      'section>admin-main-title>h2,section>admin-secondary-title>h3,section>admin-third-title>h4',
    );
    this.children.forEach((child) => {
      this.setIcon(child);
    });
    this.createPath();
  }

  setIcon(element: Element): void {
    const iconName = element.getAttribute('icon');
    if (!iconName) {
      return;
    }
    const container = this.document.createElement('div');
    container.className = 'icon-container';
    const parent = element.parentElement!;

    const iconElem = this.document.createElement('img');
    const iconPath = '/titleSvgs/' + iconName;
    iconElem.src = iconPath;
    iconElem.alt = 'icon';
    iconElem.className = 'title-icon';

    if (element.tagName === 'H2') {
      container.className += ' main-title-icon';
      const layout = this.document.createElement('template');
      layout.innerHTML = TitleLayout.MainTitleLayout.trim();
      layout.className = 'layout';
      container.innerHTML = layout.innerHTML;
      parent.classList.add('orange');
      container.appendChild(iconElem);
    }
    if (element.tagName === 'H3') {
      container.className += ' secondary-title-icon';
      const layout = this.document.createElement('template');
      layout.innerHTML = TitleLayout.SecondaryTitleLayout.trim();
      layout.className = 'layout';
      container.innerHTML = layout.innerHTML;
      parent.classList.add('orange');
      container.appendChild(iconElem);
    }
    if (element.tagName === 'H4') {
      container.className += ' sub-title-icon';
      const layout = this.document.createElement('template');
      layout.innerHTML = TitleLayout.SubTitleLayout.trim();
      layout.className = 'layout';
      container.innerHTML = layout.innerHTML;
      parent.classList.add('orange-light');
    }

    parent.className += ' title-section';
    parent.appendChild(container);
  }

  createPath(): void {
    const firstElement = this.children[0];
    const lastElement = this.children[this.children.length - 1];
    if (this.path) {
      this.path.remove();
      this.plainPath.remove();
    }

    this.path = this.document.createElement('div');
    this.path.style.setProperty('height', `calc(100% - ${lastElement.clientHeight}px)`);
    this.path.style.setProperty('width', '1.5px');
    this.path.style.setProperty('border-left', '1.5px solid #D17A2A30');
    this.path.style.setProperty('border-left-style', 'inset');
    this.path.style.setProperty('z-index', '0');
    this.path.style.setProperty('position', 'absolute');
    this.path.style.setProperty('left', '-64px');
    this.path.style.setProperty('top', '0');
    this.path.className = 'background-path';

    this.plainPath = this.document.createElement('div');
    this.plainPath.style.setProperty('top', firstElement.clientHeight + 'px');
    this.plainPath.className = 'plain-path';

    this.element.appendChild(this.path);
    this.element.appendChild(this.plainPath);
  }

  @HostListener('window:scroll')
  windowListener(): void {
    if (!this.path || !this.children || this.children.length === 0) {
      return;
    }
    this.updatePath(this.plainPath);
    this.updateIcons();
  }

  @HostListener('window:resize')
  windowResizeListener(): void {
    if (!this.children || this.children.length === 0) {
      return;
    }
    this.createPath();
    this.updatePath(this.plainPath);
    this.updateIcons();
  }

  updatePath(plainPath: HTMLElement): void {
    const firstElement = this.children[0] as HTMLElement;
    const lastElement = this.children[this.children.length - 1] as HTMLElement;
    const maxHeight =
      this.element.clientHeight - lastElement.clientHeight - firstElement.clientHeight / 2;
    // Check if the top of the element has crossed the middle of the viewport
    const elementTopRelativeToViewport = this.element.getBoundingClientRect().top;
    const hasCrossMiddle = elementTopRelativeToViewport <= window.innerHeight / 2;
    // Calculate height so the bottom of the element is at the center of the window
    // Distance from element top to window center
    let height = window.innerHeight / 2 - elementTopRelativeToViewport;
    const atBottom = window.innerHeight + window.scrollY >= this.document.body.offsetHeight - 10;

    if (!hasCrossMiddle) {
      height = 0;
    }
    if (height > maxHeight || atBottom) {
      height = maxHeight;
    }
    plainPath.style.setProperty('height', height + 'px');
  }

  updateIcons(): void {
    this.children.forEach((child) => {
      this.updateIcon(child as HTMLElement);
    });
  }

  updateIcon(container: HTMLElement): void {
    const atBottom = window.innerHeight + window.scrollY >= this.document.body.offsetHeight - 10;
    const elementTopRelativeToViewport = this.element.getBoundingClientRect().top;
    const hasCrossMiddle = elementTopRelativeToViewport <= window.innerHeight / 2;

    if (hasCrossMiddle || atBottom) {
      container.parentElement!.classList.add('cross-middle');
      if (!this.currentActiveTitle || this.currentActiveTitle !== container.id) {
        this.currentActiveTitle = container.id;
        this.summaryEventsService.setActive(container.id);
      }
    } else {
      container.parentElement!.classList.remove('cross-middle');
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }
}

export class TitleLayout {
  public static MainTitleLayout = `<svg width="72" height="72" viewBox="0 0 72 72" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="36" cy="36" r="35.5" stroke="#000"/>
    <circle class="rotate" opacity="0.5" cx="36" cy="36" r="27.5" stroke="#000" stroke-dasharray="4 4"/>
    </svg>`;

  public static SecondaryTitleLayout = `<svg width="72" height="72" viewBox="0 0 72 72" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="36" cy="36" r="35.5" stroke="#000"/>
    </svg>`;

  public static SubTitleLayout = `<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="10" cy="10" r="9.5" stroke="#000"/>
        <circle cx="10" cy="10" r="4" fill="#000" stroke="#000">
    </svg>`;
}
